import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { inputClass, labelClass } from "./classes";

/**
 * A labelled field: the label is always visible, the description and the
 * error are wired with aria-describedby, an error is announced (role
 * alert) and marks the field invalid; nothing is placeholder-only.
 */
export function TextField({ label, description, error, id, className = "", ...props }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode; error?: ReactNode }) {
  const generated = useId();
  const inputId = id ?? generated;
  const descId = `${inputId}-desc`;
  const errId = `${inputId}-err`;
  const describedBy = [description ? descId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label htmlFor={inputId} className={labelClass}>
        {label}
      </label>
      {description && (
        <p id={descId} className="text-sm text-muted">
          {description}
        </p>
      )}
      <input id={inputId} className={`${inputClass} ${error ? "border-danger" : ""}`} aria-describedby={describedBy} aria-invalid={error ? true : undefined} {...props} />
      {error && (
        <p id={errId} role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
